import * as THREE from 'three';

// ── Reusable temp scratch (zero-alloc hot path) ─────────────────────────────
const _localPoint = new THREE.Vector3();
const _segOrigin = new THREE.Vector3();
const _segDir = new THREE.Vector3();
const _scale = new THREE.Vector3();

/**
 * Oriented-bounding-box (OBB) collision primitive backed by an Object3D node
 * from a GLB. Supports two source conventions:
 *
 *   - Mesh: uses `geometry.boundingBox` for local extents.
 *   - Empty (no geometry, e.g. a Blender cube-display empty): assumes a unit
 *     cube of local extents [-0.5, 0.5]³. The empty's display size is encoded
 *     in the node's scale, which is folded into the world half-extents.
 *
 * Per frame, call `update()` to refresh the cached world transform from the
 * node's `matrixWorld`. Then `containsPoint` / `intersectsSegment` are pure
 * analytic tests with no further matrix math.
 *
 * Designed to be weapon-agnostic — used by GAU-8 bullets today, missiles and
 * other projectiles in the future.
 */
export default class HitVolume {
  /**
   * @param {THREE.Object3D} node — the "Hitbox" node from the model.
   */
  constructor(node) {
    this.node = node;

    // Local box half-extents (before scale). Source depends on node type.
    this._localHalf = new THREE.Vector3(0.5, 0.5, 0.5);
    // Local box center (before scale). Usually origin, but Mesh bbox may be offset.
    this._localCenter = new THREE.Vector3(0, 0, 0);

    if (node.isMesh && node.geometry) {
      const geom = node.geometry;
      if (!geom.boundingBox) geom.computeBoundingBox();
      const bb = geom.boundingBox;
      this._localHalf.set(
        (bb.max.x - bb.min.x) * 0.5,
        (bb.max.y - bb.min.y) * 0.5,
        (bb.max.z - bb.min.z) * 0.5,
      );
      this._localCenter.set(
        (bb.max.x + bb.min.x) * 0.5,
        (bb.max.y + bb.min.y) * 0.5,
        (bb.max.z + bb.min.z) * 0.5,
      );
    }
    // else: empty — keep the default unit-cube half-extents and zero center.

    // Cached world-space state, refreshed by update().
    this.center = new THREE.Vector3();
    this.halfExtents = new THREE.Vector3();
    this.worldQuat = new THREE.Quaternion();
    this.invQuat = new THREE.Quaternion();
    this.boundingRadius = 0;
    this.boundingRadiusSq = 0;

    this.update();
  }

  /** Refresh cached world OBB from the node's transform. Cheap. */
  update() {
    // Make sure matrixWorld reflects ancestors' current transforms. The render
    // loop also does this, but we run earlier in the frame.
    this.node.updateWorldMatrix(true, false);

    this.node.matrixWorld.decompose(this.center, this.worldQuat, _scale);

    // World half-extents = local half × |scale| (componentwise).
    this.halfExtents.set(
      this._localHalf.x * Math.abs(_scale.x),
      this._localHalf.y * Math.abs(_scale.y),
      this._localHalf.z * Math.abs(_scale.z),
    );

    // Offset world center by the local-center delta rotated into world.
    if (this._localCenter.x !== 0 || this._localCenter.y !== 0 || this._localCenter.z !== 0) {
      _localPoint.copy(this._localCenter)
        .multiply(_scale)
        .applyQuaternion(this.worldQuat);
      this.center.add(_localPoint);
    }

    this.invQuat.copy(this.worldQuat).invert();

    // Bounding sphere radius for cheap broad-phase reject.
    this.boundingRadiusSq =
      this.halfExtents.x * this.halfExtents.x +
      this.halfExtents.y * this.halfExtents.y +
      this.halfExtents.z * this.halfExtents.z;
    this.boundingRadius = Math.sqrt(this.boundingRadiusSq);
  }

  /**
   * Point-in-OBB test. Sphere broad-phase reject, then transform into local
   * space and AABB compare.
   */
  containsPoint(x, y, z) {
    const dx = x - this.center.x;
    const dy = y - this.center.y;
    const dz = z - this.center.z;
    if (dx * dx + dy * dy + dz * dz > this.boundingRadiusSq) return false;

    _localPoint.set(dx, dy, dz).applyQuaternion(this.invQuat);
    const h = this.halfExtents;
    return (
      _localPoint.x >= -h.x && _localPoint.x <= h.x &&
      _localPoint.y >= -h.y && _localPoint.y <= h.y &&
      _localPoint.z >= -h.z && _localPoint.z <= h.z
    );
  }

  /**
   * Segment-vs-OBB test using the slabs algorithm. Used to prevent fast
   * projectiles from tunneling through small hitboxes between frames.
   *
   * @param {number} ox  segment origin x (world)
   * @param {number} oy  segment origin y (world)
   * @param {number} oz  segment origin z (world)
   * @param {number} dx  segment direction x (world, NOT normalized — full delta)
   * @param {number} dy  segment direction y (world)
   * @param {number} dz  segment direction z (world)
   * @returns {{ x:number, y:number, z:number, t:number } | null}
   *          Hit point in world space and parametric t ∈ [0,1] along the
   *          segment, or null if no intersection.
   */
  intersectsSegment(ox, oy, oz, dx, dy, dz) {
    // Broad phase: distance from segment to OBB center > boundingRadius.
    // Cheap conservative reject — closest point on segment to center.
    const fx = ox - this.center.x;
    const fy = oy - this.center.y;
    const fz = oz - this.center.z;
    const segLen2 = dx * dx + dy * dy + dz * dz;
    let tProj = 0;
    if (segLen2 > 0) {
      tProj = -(fx * dx + fy * dy + fz * dz) / segLen2;
      if (tProj < 0) tProj = 0;
      else if (tProj > 1) tProj = 1;
    }
    const cx = fx + dx * tProj;
    const cy = fy + dy * tProj;
    const cz = fz + dz * tProj;
    if (cx * cx + cy * cy + cz * cz > this.boundingRadiusSq) return null;

    // Transform segment into local box space.
    _segOrigin.set(fx, fy, fz).applyQuaternion(this.invQuat);
    _segDir.set(dx, dy, dz).applyQuaternion(this.invQuat);

    const h = this.halfExtents;
    let tMin = 0;
    let tMax = 1;

    // Slabs: per-axis interval intersection.
    // X axis
    if (Math.abs(_segDir.x) < 1e-8) {
      if (_segOrigin.x < -h.x || _segOrigin.x > h.x) return null;
    } else {
      const inv = 1 / _segDir.x;
      let t1 = (-h.x - _segOrigin.x) * inv;
      let t2 = ( h.x - _segOrigin.x) * inv;
      if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
      if (t1 > tMin) tMin = t1;
      if (t2 < tMax) tMax = t2;
      if (tMin > tMax) return null;
    }
    // Y axis
    if (Math.abs(_segDir.y) < 1e-8) {
      if (_segOrigin.y < -h.y || _segOrigin.y > h.y) return null;
    } else {
      const inv = 1 / _segDir.y;
      let t1 = (-h.y - _segOrigin.y) * inv;
      let t2 = ( h.y - _segOrigin.y) * inv;
      if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
      if (t1 > tMin) tMin = t1;
      if (t2 < tMax) tMax = t2;
      if (tMin > tMax) return null;
    }
    // Z axis
    if (Math.abs(_segDir.z) < 1e-8) {
      if (_segOrigin.z < -h.z || _segOrigin.z > h.z) return null;
    } else {
      const inv = 1 / _segDir.z;
      let t1 = (-h.z - _segOrigin.z) * inv;
      let t2 = ( h.z - _segOrigin.z) * inv;
      if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
      if (t1 > tMin) tMin = t1;
      if (t2 < tMax) tMax = t2;
      if (tMin > tMax) return null;
    }

    const t = tMin;
    return {
      x: ox + dx * t,
      y: oy + dy * t,
      z: oz + dz * t,
      t,
    };
  }
}
